/**
 * rmcplotsExcelReader.ts — read RMC150 shear-machine sample rows from an
 * Excel workbook (2026-09-29).
 *
 * The lab sometimes opens the native `.rmcplots` XML in Excel (e.g. to apply
 * a stiffness correction) and saves it as `.xlsx`. Excel flattens the XML
 * into one sheet: a header row naming every XML attribute (`Name`, `Comment`,
 * … `Int`, `Item0` … `Item4`) followed by one row per element. The first
 * rows carry only controller / quantity metadata; the `<Sample>` rows are the
 * ones whose `Int` + `Item0` … `Item3` cells are filled.
 *
 * This reader locates that header row by name (so column order does not
 * matter), keeps every row whose four reading cells are numeric, and hands
 * the result to the SAME cleanup as the XML path (`cleanRmcplotsRows`).
 * exceljs is loaded lazily — the browser bundle is large and only needed
 * when a workbook is actually uploaded.
 */
import type { CellValue, Worksheet } from 'exceljs';
import type { RawRmcplotsRow } from './rmcplotsParser';

/** Header names Excel writes for the `<Sample>` attributes (case-insensitive). */
const REQUIRED_COLUMNS = ['item0', 'item1', 'item2', 'item3'] as const;
const INT_COLUMN = 'int';
/** Rows scanned for the header before giving up on a sheet. */
const HEADER_SCAN_ROWS = 50;

type ColumnMap = Record<(typeof REQUIRED_COLUMNS)[number], number> & { int: number | null };

const cellText = (value: CellValue): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if ('richText' in value) return value.richText.map((t) => t.text).join('');
    if ('result' in value) return cellText(value.result as CellValue);
    if ('text' in value) return typeof value.text === 'string' ? value.text : cellText(value.text as CellValue);
  }
  return null;
};

/** A numeric cell value, or null for blanks / text / errors. */
export const cellNumber = (value: CellValue): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === 'object' && 'result' in value) return cellNumber(value.result as CellValue);
  return null;
};

const findHeaderRow = (ws: Worksheet): { headerRow: number; columns: ColumnMap } | null => {
  const last = Math.min(ws.rowCount, HEADER_SCAN_ROWS);
  for (let r = 1; r <= last; r++) {
    const row = ws.getRow(r);
    const byName = new Map<string, number>();
    row.eachCell({ includeEmpty: false }, (cell, col) => {
      const text = cellText(cell.value)?.trim().toLowerCase();
      if (text && !byName.has(text)) byName.set(text, col);
    });
    if (REQUIRED_COLUMNS.every((c) => byName.has(c))) {
      return {
        headerRow: r,
        columns: {
          item0: byName.get('item0')!,
          item1: byName.get('item1')!,
          item2: byName.get('item2')!,
          item3: byName.get('item3')!,
          int: byName.get(INT_COLUMN) ?? null,
        },
      };
    }
  }
  return null;
};

const readSheetRows = (ws: Worksheet, headerRow: number, columns: ColumnMap): RawRmcplotsRow[] => {
  const rows: RawRmcplotsRow[] = [];
  for (let r = headerRow + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const item0 = cellNumber(row.getCell(columns.item0).value);
    const item1 = cellNumber(row.getCell(columns.item1).value);
    const item2 = cellNumber(row.getCell(columns.item2).value);
    const item3 = cellNumber(row.getCell(columns.item3).value);
    // Metadata rows (controller / quantity definitions) leave the reading cells blank.
    if (item0 === null || item1 === null || item2 === null || item3 === null) continue;

    const intCell = columns.int === null ? null : cellNumber(row.getCell(columns.int).value);
    const intVal = intCell === null ? rows.length : Math.trunc(intCell);
    rows.push({
      intIndex: intVal,
      reading: {
        sampleInt: intVal,
        horizontalDisplacement: item0,
        horizontalLoad: item1,
        verticalDisplacement: item2,
        verticalLoad: item3,
      },
    });
  }
  return rows;
};

/**
 * Extract the raw sample rows from an Excel-saved .rmcplots file. Uses the
 * first worksheet that carries an `Item0` … `Item3` header row.
 */
export const readRmcplotsExcelRows = async (file: File): Promise<RawRmcplotsRow[]> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch (err) {
    throw new Error(`Could not open "${file.name}" as an Excel workbook: ${err instanceof Error ? err.message : 'unknown error'}`);
  }

  for (const ws of workbook.worksheets) {
    const header = findHeaderRow(ws);
    if (header) return readSheetRows(ws, header.headerRow, header.columns);
  }
  throw new Error(
    `No Int / Item0–Item3 columns found in "${file.name}". ` +
    'Expected the .rmcplots file opened in Excel and saved as .xlsx (header row with Item0, Item1, Item2, Item3).',
  );
};
